import { AppDataProvider } from '@app/core/app-data';
import { webApi } from '@mi-front/api-clients';
import { Station } from '@mi-front/core';
import { SplashLoader } from '@mi-front/ui';
import debugFn from 'debug';
import { ReactElement, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { authErrorMessages } from '../../errors/auth-errors';
import { useSuperadminLock } from '../../impersonation/hooks/useSuperadminLock';
import { useToken } from '../hooks/useToken';
import { AuthContextInit } from './AuthContextInit';
import { NoStationsPage } from './NoStationsPage';
import { useImpersonationInit } from './useImpersonationInit';
import { useLogout } from './useLogout';

export const debug = debugFn('auth:AuthBootstrap');

export function AppBootstrap({ children }: { children: ReactElement }) {
  const history = useHistory();
  const logoutFn = useLogout();
  const token = useToken();

  // Set ApiClient auth for impersonation before the app-init query runs
  // so proper data is returned from the API
  useImpersonationInit();

  const {
    data: appInitResponse,
    isError,
    error,
  } = webApi.useGetAppInitInit({
    query: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: (query) => (query.state.data?.stations?.length ? false : 10000),
    },
  });

  useSuperadminLock(appInitResponse?.user);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    debug('Error fetching app init from API', error);
    const code = (error as { code?: string })?.code;
    const msgCode = authErrorMessages[code as keyof typeof authErrorMessages]
      ? code
      : 'ERR_AUTH_FAILED';

    logoutFn({
      returnTo: `/login?login_error=${msgCode}`,
    });
  }, [isError, error, history]);

  if (!token || !appInitResponse) {
    return <SplashLoader />;
  }

  if (appInitResponse?.user.role !== 'SUPERADMIN' && !appInitResponse.stations?.length) {
    return <NoStationsPage />;
  }

  return (
    <AuthContextInit appInitResponse={appInitResponse}>
      <AppDataProvider
        stations={(appInitResponse.stations as Station[]) ?? []}
        stationsGroups={appInitResponse.stationsGroups ?? []}
      >
        {children}
      </AppDataProvider>
    </AuthContextInit>
  );
}
